import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Modal,
  TextInput,
  Text,
  Stack,
  Group,
  Button,
  Box,
  ScrollArea,
  UnstyledButton,
  CloseButton,
  Badge,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { ExpenseKind, ExpenseCategory } from "@/utils/api/AppEssentialsService";

import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

interface ExpenseKindPickerModalBaseProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Callback to close the modal */
  onClose: () => void;

  /** Quick access expense codes to show as chips */
  quickAccessCodes?: string[];

  /** Whether to show only schedulable kinds */
  schedulableOnly?: boolean;
}

export interface ExpenseKindPickerModalSingleProps
  extends ExpenseKindPickerModalBaseProps {
  multiSelect?: false;

  /** Currently selected kind ID */
  value: number | null;

  /** Callback when a kind is selected */
  onSelect: (kindId: number | null) => void;
}

export interface ExpenseKindPickerModalMultiProps
  extends ExpenseKindPickerModalBaseProps {
  multiSelect: true;

  /** Currently selected kind IDs */
  value: number[];

  /** Callback when selection is confirmed */
  onSelect: (kindIds: number[]) => void;

  /** Maximum number of selections allowed */
  maxSelections?: number;
}

export type ExpenseKindPickerModalProps =
  | ExpenseKindPickerModalSingleProps
  | ExpenseKindPickerModalMultiProps;

// ============================================
// Constants
// ============================================

const ICON_SIZES = {
  search: 20,
  categoryHeader: 18,
  kindOption: 20,
  maintenanceBadge: 16,
  quickAccess: 16,
  selectedCheck: 18,
};

const CATEGORY_ICONS: Record<string, string> = {
  MAINTENANCE: "CarGarage",
  REPAIRS: "Tool",
  OTHER: "Dots",
};

const KIND_ICONS: Record<string, string> = {
  parking: "Parking",
  car_wash: "Droplet",
  toll: "Road",
  oil_change: "Oil",
  tire_rotation: "Circle",
  insurance: "Shield",
  registration: "FileText",
  inspection: "ClipboardCheck",
};

const FUSE_OPTIONS_KINDS: IFuseOptions<ExpenseKind> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "code", weight: 0.3 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

const FUSE_OPTIONS_CATEGORIES: IFuseOptions<ExpenseCategory> = {
  keys: [{ name: "name", weight: 1.0 }],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Helper Functions
// ============================================

const getIconForKind = (code: string | undefined): string => {
  if (!code) return "Receipt";
  return KIND_ICONS[code.toLowerCase()] || "Receipt";
};

const getIconForCategory = (code: string | undefined): string => {
  if (!code) return "Folder";
  return CATEGORY_ICONS[code.toUpperCase()] || "Folder";
};

// ============================================
// Component
// ============================================

export const ExpenseKindPickerModal: React.FC<ExpenseKindPickerModalProps> = (
  props
) => {
  const { opened, onClose, quickAccessCodes = [], schedulableOnly } = props;

  const { t } = useTranslation(["expenses", "common"]);
  const { expenseCategories, expenseKinds } = useAppEssentialsContext();

  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isMulti = props.multiSelect === true;

  // For multi-select: track a pending selection internally until "Done" is pressed
  const [pendingValues, setPendingValues] = useState<number[]>([]);

  // Sync pending values when modal opens
  useEffect(() => {
    if (opened) {
      if (isMulti) {
        setPendingValues((props as ExpenseKindPickerModalMultiProps).value);
      }
    } else {
      setSearchValue("");
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxSelections = isMulti
    ? (props as ExpenseKindPickerModalMultiProps).maxSelections
    : undefined;

  // Get quick access kinds
  const quickAccessKinds = useMemo(() => {
    if (!quickAccessCodes.length) return [];
    return quickAccessCodes
      .map((code) =>
        expenseKinds.find((k) => k.code?.toUpperCase() === code.toUpperCase())
      )
      .filter((k): k is ExpenseKind => k !== undefined);
  }, [quickAccessCodes, expenseKinds]);

  // Build Fuse indexes for fuzzy search
  const categoryFuse = useMemo(
    () => new Fuse(expenseCategories, FUSE_OPTIONS_CATEGORIES),
    [expenseCategories]
  );

  const kindFuseByCategory = useMemo(() => {
    const map = new Map<string | number, Fuse<ExpenseKind>>();
    for (const category of expenseCategories) {
      if (category.kinds && category.kinds.length > 0) {
        map.set(
          category.id || "",
          new Fuse(category.kinds, FUSE_OPTIONS_KINDS)
        );
      }
    }
    return map;
  }, [expenseCategories]);

  // Filter categories and kinds based on fuzzy search
  const filteredCategories = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return expenseCategories;

    const matchedCategoryIds = new Set(
      categoryFuse.search(search).map((result) => result.item.id)
    );

    return expenseCategories
      .map((category) => {
        if (matchedCategoryIds.has(category.id)) return category;

        const fuse = kindFuseByCategory.get(category.id || "");
        if (!fuse) return null;

        const matchingKinds = fuse.search(search).map((result) => result.item);
        if (matchingKinds.length > 0) return { ...category, kinds: matchingKinds };

        return null;
      })
      .filter((c): c is ExpenseCategory => c !== null);
  }, [expenseCategories, searchValue, categoryFuse, kindFuseByCategory]);

  // ---- Single-select handlers ----

  const handleSingleSelect = useCallback(
    (kindId: number | undefined) => {
      if (kindId !== undefined && !isMulti) {
        (props as ExpenseKindPickerModalSingleProps).onSelect(Number(kindId));
        onClose();
      }
    },
    [isMulti, props, onClose]
  );

  const handleSingleQuickSelect = useCallback(
    (kind: ExpenseKind) => {
      if (kind.id !== undefined && !isMulti) {
        (props as ExpenseKindPickerModalSingleProps).onSelect(Number(kind.id));
        onClose();
      }
    },
    [isMulti, props, onClose]
  );

  // ---- Multi-select handlers ----

  const isKindSelected = useCallback(
    (kindId: number | undefined): boolean => {
      if (kindId === undefined) return false;
      if (isMulti) return pendingValues.includes(Number(kindId));
      return (props as ExpenseKindPickerModalSingleProps).value === kindId;
    },
    [isMulti, pendingValues, props]
  );

  const handleMultiToggle = useCallback(
    (kindId: number | undefined) => {
      if (kindId === undefined) return;
      const id = Number(kindId);
      setPendingValues((prev) => {
        if (prev.includes(id)) {
          return prev.filter((v) => v !== id);
        }
        if (maxSelections !== undefined && prev.length >= maxSelections) {
          return prev;
        }
        return [...prev, id];
      });
    },
    [maxSelections]
  );

  const handleMultiQuickToggle = useCallback(
    (kind: ExpenseKind) => {
      if (kind.id !== undefined) handleMultiToggle(Number(kind.id));
    },
    [handleMultiToggle]
  );

  const handleMultiClearAll = useCallback(() => {
    setPendingValues([]);
  }, []);

  const handleMultiConfirm = useCallback(() => {
    (props as ExpenseKindPickerModalMultiProps).onSelect(pendingValues);
    onClose();
  }, [props, pendingValues, onClose]);

  // ---- Shared handlers ----

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.currentTarget.value);
    },
    []
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    searchInputRef.current?.focus();
  }, []);

  const hasResults = filteredCategories.some(
    (c) => c.kinds && c.kinds.length > 0
  );

  const isAtMax =
    isMulti &&
    maxSelections !== undefined &&
    pendingValues.length >= maxSelections;

  // ---- Footer for multi-select ----
  const multiFooter = isMulti ? (
    <Box
      px="md"
      py="sm"
      style={{
        flexShrink: 0,
        borderTop: "1px solid var(--mantine-color-gray-3)",
      }}
    >
      <Group justify="space-between" align="center">
        <Group gap="xs" align="center">
          {pendingValues.length > 0 && (
            <>
              <Badge variant="filled" size="sm" radius="sm">
                {pendingValues.length}
                {maxSelections !== undefined ? ` / ${maxSelections}` : ""}
              </Badge>
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                onClick={handleMultiClearAll}
              >
                {t("common:form.clearAll", { defaultValue: "Clear all" })}
              </Button>
            </>
          )}
        </Group>
        <Button onClick={handleMultiConfirm} size="sm">
          {t("common:form.done", { defaultValue: "Done" })}
        </Button>
      </Group>
    </Box>
  ) : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      title={t("expenses:form.fields.expenseKind.modalTitle", {
        defaultValue: t("expenses:form.fields.expenseKind.label"),
      })}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100dvh - 60px)",
          padding: 0,
          overflow: "hidden",
        },
        header: {
          padding: "12px 16px",
        },
        content: {
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Sticky Search Bar */}
      <Box
        px="md"
        py="sm"
        style={{
          flexShrink: 0,
          borderBottom: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        <TextInput
          ref={searchInputRef}
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t("expenses:form.fields.expenseKind.searchPlaceholder", {
            defaultValue: t("expenses:form.fields.expenseKind.placeholder"),
          })}
          leftSection={<AppIcon iconName="Search" size={ICON_SIZES.search} />}
          rightSection={
            searchValue ? (
              <CloseButton
                size="sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClearSearch}
                aria-label={t("common:form.clearSearch")}
              />
            ) : undefined
          }
          size="md"
          autoComplete="off"
        />
      </Box>

      {/* Quick Access Chips — shown when no search query */}
      {quickAccessKinds.length > 0 && !searchValue && (
        <Box
          px="md"
          py="xs"
          style={{
            flexShrink: 0,
            borderBottom: "1px solid var(--mantine-color-gray-2)",
          }}
        >
          <Group gap="xs" wrap="wrap">
            {quickAccessKinds.map((kind) => (
              <Button
                key={kind.id}
                variant={isKindSelected(kind.id) ? "filled" : "light"}
                size="xs"
                leftSection={
                  <AppIcon
                    iconName={getIconForKind(kind.code)}
                    size={ICON_SIZES.quickAccess}
                  />
                }
                onClick={() =>
                  isMulti
                    ? handleMultiQuickToggle(kind)
                    : handleSingleQuickSelect(kind)
                }
              >
                {kind.name}
              </Button>
            ))}
          </Group>
        </Box>
      )}

      {/* Scrollable Options List */}
      <ScrollArea
        style={{ flex: 1, minHeight: 0 }}
        type="scroll"
        offsetScrollbars
      >
        <Stack gap={0} pb="md">
          {/* No results message */}
          {!hasResults && searchValue && (
            <Box px="md" py="xl">
              <Text c="dimmed" ta="center" size="sm">
                {t("expenses:form.fields.expenseKind.noResults")}
              </Text>
            </Box>
          )}

          {/* Grouped options by category */}
          {filteredCategories.map((category) => {
            if (!category.kinds || category.kinds.length === 0) return null;

            const categoryKinds = schedulableOnly
              ? category.kinds // TODO: .filter(kind => kind.canSchedule)
              : category.kinds;

            if (categoryKinds.length === 0) return null;

            return (
              <Box key={category.id}>
                {/* Category Header */}
                <Group
                  gap="xs"
                  px="md"
                  py="xs"
                  bg="gray.0"
                  style={{ position: "sticky", top: 0, zIndex: 1 }}
                >
                  <AppIcon
                    iconName={getIconForCategory(category.code)}
                    size={ICON_SIZES.categoryHeader}
                  />
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    {category.name}
                  </Text>
                </Group>

                {/* Kind Items */}
                {categoryKinds.map((kind) => {
                  const isSelected = isKindSelected(kind.id);
                  const isDisabledByMax = isMulti && isAtMax && !isSelected;

                  return (
                    <UnstyledButton
                      key={kind.id}
                      onClick={() =>
                        isMulti
                          ? handleMultiToggle(kind.id)
                          : handleSingleSelect(kind.id)
                      }
                      w="100%"
                      px="md"
                      py="sm"
                      disabled={isDisabledByMax}
                      style={(theme) => ({
                        backgroundColor: isSelected
                          ? `var(--mantine-color-${theme.primaryColor}-0)`
                          : undefined,
                        borderBottom: "1px solid var(--mantine-color-gray-1)",
                        opacity: isDisabledByMax ? 0.45 : 1,
                        cursor: isDisabledByMax ? "default" : "pointer",
                      })}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <AppIcon
                          iconName={getIconForKind(kind.code)}
                          size={ICON_SIZES.kindOption}
                        />
                        <Text size="md" fw={isSelected ? 600 : 400}>
                          {kind.name}
                        </Text>

                        {kind.isItMaintenance && (
                          <AppIcon
                            iconName="Tool"
                            size={ICON_SIZES.maintenanceBadge}
                            color="dimmed"
                          />
                        )}

                        {isSelected && (
                          <Box ml="auto">
                            <AppIcon
                              iconName="Check"
                              size={ICON_SIZES.selectedCheck}
                              color="var(--mantine-color-blue-6)"
                            />
                          </Box>
                        )}
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </Box>
            );
          })}
        </Stack>
      </ScrollArea>

      {/* Multi-select footer with count + Done button */}
      {multiFooter}
    </Modal>
  );
};